import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  Building2, Clock, FileText, Image, Megaphone, Info,
  Save, Plus, Trash2, MapPin, Phone, Globe, Tag,
  Wifi, Car, CreditCard, CalendarCheck, Accessibility,
  Loader2, ExternalLink, X, RefreshCw, LogOut, CheckCircle2,
  ChevronRight, Store
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';

const DAYS = [
  { key: 'monday', label: 'Lunedi' },
  { key: 'tuesday', label: 'Martedi' },
  { key: 'wednesday', label: 'Mercoledi' },
  { key: 'thursday', label: 'Giovedi' },
  { key: 'friday', label: 'Venerdi' },
  { key: 'saturday', label: 'Sabato' },
  { key: 'sunday', label: 'Domenica' }
];

const ATTRIBUTE_CONFIG = [
  { key: 'wheelchair_accessible', label: 'Accessibile in sedia a rotelle', icon: Accessibility },
  { key: 'wifi', label: 'Wi-Fi disponibile', icon: Wifi },
  { key: 'parking', label: 'Parcheggio', icon: Car },
  { key: 'accepts_cards', label: 'Accetta carte', icon: CreditCard },
  { key: 'appointment_required', label: 'Appuntamento richiesto', icon: CalendarCheck }
];

const GoogleBusinessPage = () => {
  const { API, getAuthHeaders } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [newPostDialog, setNewPostDialog] = useState(false);
  const [newPhotoUrl, setNewPhotoUrl] = useState('');
  const [connectionStatus, setConnectionStatus] = useState(null);

  // Location selector state
  const [locationDialog, setLocationDialog] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [selectingLocation, setSelectingLocation] = useState(false);

  const [newPost, setNewPost] = useState({
    content: '', post_type: 'update', cta_type: '', cta_url: '',
    media_url: '', event_title: '', event_start: '', event_end: ''
  });

  const fetchProfile = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/google-business/profile`, getAuthHeaders());
      setProfile(res.data);
    } catch (e) { console.error('Errore profilo GB:', e); }
  }, [API, getAuthHeaders]);

  const fetchPosts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/google-business/posts`, getAuthHeaders());
      setPosts(res.data);
    } catch (e) { console.error('Errore post GB:', e); }
  }, [API, getAuthHeaders]);

  const fetchConnectionStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/google-business/connection`, getAuthHeaders());
      setConnectionStatus(res.data);
      return res.data;
    } catch (e) { console.error('Errore stato connessione:', e); return null; }
  }, [API, getAuthHeaders]);

  useEffect(() => {
    const connected = searchParams.get('connected');
    const error = searchParams.get('error');
    const errorDetail = searchParams.get('detail');
    if (connected === 'true') {
      toast.success('Account Google collegato! Ora seleziona l\'attivita da gestire.');
      setSearchParams({});
      // Auto-open location selector after connection
      setTimeout(() => setLocationDialog(true), 500);
    } else if (error) {
      const detailMsg = errorDetail ? ` (${errorDetail.replace(/_/g, ' ')})` : '';
      toast.error(`Errore connessione Google: ${error}${detailMsg}`);
      setSearchParams({});
    }

    Promise.all([fetchProfile(), fetchPosts(), fetchConnectionStatus()])
      .then(([, , connStatus]) => {
        setLoading(false);
        // Auto-open location selector if connected but no location selected
        if (connStatus?.connected && connStatus?.needs_location_selection) {
          setTimeout(() => setLocationDialog(true), 300);
        }
      });
  }, [searchParams, setSearchParams, fetchProfile, fetchPosts, fetchConnectionStatus]);

  // ── Account & Location discovery ──
  const fetchAccounts = async () => {
    setLoadingAccounts(true);
    setAccounts([]);
    setLocations([]);
    setSelectedAccount(null);
    try {
      const res = await axios.get(`${API}/google-business/accounts`, getAuthHeaders());
      setAccounts(res.data.accounts || []);
      if ((res.data.accounts || []).length === 0) {
        toast.error('Nessun account Google Business trovato per questo account Google.');
      }
    } catch (e) {
      const detail = e.response?.data?.detail || 'Errore nel caricamento account';
      toast.error(detail);
    } finally { setLoadingAccounts(false); }
  };

  const fetchLocations = async (accountName) => {
    setLoadingLocations(true);
    setLocations([]);
    try {
      const res = await axios.get(`${API}/google-business/locations`, {
        ...getAuthHeaders(),
        params: { account_name: accountName }
      });
      setLocations(res.data.locations || []);
      if ((res.data.locations || []).length === 0) {
        toast.info('Nessuna attivita trovata in questo account.');
      }
    } catch (e) {
      toast.error('Errore nel caricamento attivita');
    } finally { setLoadingLocations(false); }
  };

  const selectLocation = async (loc) => {
    setSelectingLocation(true);
    try {
      const res = await axios.post(`${API}/google-business/select-location`, {
        location_name: loc.name,
        account_name: selectedAccount?.name || ''
      }, getAuthHeaders());
      setProfile(res.data);
      setLocationDialog(false);
      await fetchConnectionStatus();
      toast.success(`Attivita "${loc.title || 'Selezionata'}" collegata e sincronizzata!`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nella selezione');
    } finally { setSelectingLocation(false); }
  };

  const handleOpenLocationSelector = () => {
    setLocationDialog(true);
    fetchAccounts();
  };

  const handleSelectAccount = (account) => {
    setSelectedAccount(account);
    fetchLocations(account.name);
  };

  // ── Other actions ──
  const connectGoogle = async () => {
    setConnecting(true);
    try {
      const res = await axios.get(`${API}/google-business/auth/url`, getAuthHeaders());
      window.location.href = res.data.auth_url;
    } catch (e) {
      toast.error('Errore nella generazione URL di autenticazione');
      setConnecting(false);
    }
  };

  const disconnectGoogle = async () => {
    if (!window.confirm('Vuoi disconnettere il tuo account Google Business?')) return;
    try {
      await axios.post(`${API}/google-business/auth/disconnect`, {}, getAuthHeaders());
      setConnectionStatus({ connected: false, needs_location_selection: false });
      fetchProfile();
      toast.success('Account Google disconnesso');
    } catch (e) {
      toast.error('Errore nella disconnessione');
    }
  };

  const syncFromGoogle = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/google-business/sync`, {}, getAuthHeaders());
      setProfile(res.data);
      toast.success('Profilo sincronizzato da Google');
    } catch (e) {
      const detail = e.response?.data?.detail || 'Errore nella sincronizzazione';
      if (detail.includes('seleziona')) {
        handleOpenLocationSelector();
      }
      toast.error(detail);
    } finally { setSyncing(false); }
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/google-business/profile`, {
        business_name: profile.business_name, category: profile.category,
        address: profile.address, phone: profile.phone,
        website: profile.website, description: profile.description
      }, getAuthHeaders());
      setProfile(res.data);
      if (res.data._google_sync_error) {
        toast.warning('Salvato localmente, ma errore sincronizzazione Google');
      } else {
        const isLinked = connectionStatus?.connected && connectionStatus?.location_name;
        toast.success(isLinked ? 'Profilo salvato e sincronizzato con Google' : 'Profilo salvato localmente');
      }
    } catch (e) { toast.error('Errore nel salvataggio'); }
    finally { setSaving(false); }
  };

  const saveHours = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/google-business/hours`, profile.opening_hours, getAuthHeaders());
      const isLinked = connectionStatus?.connected && connectionStatus?.location_name;
      toast.success(isLinked ? 'Orari aggiornati e sincronizzati con Google' : 'Orari aggiornati localmente');
    } catch (e) { toast.error('Errore nel salvataggio orari'); }
    finally { setSaving(false); }
  };

  const saveAttributes = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/google-business/attributes`, profile.attributes, getAuthHeaders());
      toast.success('Attributi aggiornati');
    } catch (e) { toast.error('Errore nel salvataggio attributi'); }
    finally { setSaving(false); }
  };

  const addPhoto = async () => {
    if (!newPhotoUrl.trim()) return;
    try {
      await axios.post(`${API}/google-business/photos`, { photo_url: newPhotoUrl }, getAuthHeaders());
      setNewPhotoUrl('');
      fetchProfile();
      toast.success('Foto aggiunta');
    } catch (e) { toast.error('Errore aggiunta foto'); }
  };

  const removePhoto = async (url) => {
    try {
      await axios.delete(`${API}/google-business/photos`, { ...getAuthHeaders(), data: { photo_url: url } });
      fetchProfile();
      toast.success('Foto rimossa');
    } catch (e) { toast.error('Errore rimozione foto'); }
  };

  const createPost = async () => {
    if (!newPost.content.trim()) return;
    try {
      await axios.post(`${API}/google-business/posts`, newPost, getAuthHeaders());
      setNewPostDialog(false);
      setNewPost({ content: '', post_type: 'update', cta_type: '', cta_url: '', media_url: '', event_title: '', event_start: '', event_end: '' });
      fetchPosts();
      toast.success('Aggiornamento creato');
    } catch (e) { toast.error('Errore creazione post'); }
  };

  const deletePost = async (id) => {
    if (!window.confirm('Eliminare questo aggiornamento?')) return;
    try {
      await axios.delete(`${API}/google-business/posts/${id}`, getAuthHeaders());
      fetchPosts();
      toast.success('Aggiornamento eliminato');
    } catch (e) { toast.error('Errore eliminazione'); }
  };

  const updateField = (field, value) => setProfile(prev => ({ ...prev, [field]: value }));
  const updateHour = (day, field, value) => setProfile(prev => ({
    ...prev, opening_hours: { ...prev.opening_hours, [day]: { ...prev.opening_hours[day], [field]: value } }
  }));
  const updateAttr = (key, value) => setProfile(prev => ({
    ...prev, attributes: { ...prev.attributes, [key]: value }
  }));

  if (loading || !profile) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const isConnected = connectionStatus?.connected;
  const hasLocation = connectionStatus?.location_name;
  const isFullyLinked = isConnected && hasLocation;

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="google-business-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-outfit">Google Business</h1>
          <p className="text-muted-foreground mt-1">Gestisci la scheda Google del tuo store</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {isConnected ? (
            <>
              <Badge className={`${isFullyLinked ? 'bg-emerald-500/10 text-emerald-600 border-emerald-200' : 'bg-amber-500/10 text-amber-600 border-amber-200'} max-w-full truncate`} data-testid="connected-badge">
                <CheckCircle2 className="w-3.5 h-3.5 mr-1.5 flex-shrink-0" />
                <span className="truncate">{connectionStatus?.google_email || 'Collegato'}</span>
              </Badge>
              {isFullyLinked && (
                <Button variant="outline" size="sm" onClick={syncFromGoogle} disabled={syncing} data-testid="sync-google-btn">
                  <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
                  Sincronizza
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={handleOpenLocationSelector} data-testid="select-location-btn">
                <Store className="w-4 h-4 mr-2" />
                {hasLocation ? 'Cambia Attivita' : 'Seleziona Attivita'}
              </Button>
              <Button variant="ghost" size="sm" onClick={disconnectGoogle} data-testid="disconnect-google-btn">
                <LogOut className="w-4 h-4 mr-2" />
                Disconnetti
              </Button>
            </>
          ) : (
            <Badge variant="outline" className="text-amber-600 border-amber-200">Non collegato</Badge>
          )}
        </div>
      </div>

      {/* Connection Banner - Not connected */}
      {!isConnected && (
        <Card className="border-amber-200 bg-amber-50/50 dark:bg-amber-950/20 dark:border-amber-800">
          <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex items-start gap-4 flex-1 min-w-0">
              <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/50 flex items-center justify-center flex-shrink-0">
                <Building2 className="w-5 h-5 text-amber-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">Collega il tuo account Google Business</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Collegando il tuo account Google, potrai sincronizzare automaticamente il profilo, gli orari e le informazioni della tua attivita.
                </p>
              </div>
            </div>
            <Button onClick={connectGoogle} disabled={connecting} data-testid="connect-google-btn" className="w-full sm:w-auto">
              {connecting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ExternalLink className="w-4 h-4 mr-2" />}
              Collega Google
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Connected but no location selected */}
      {isConnected && !hasLocation && (
        <Card className="border-amber-200 bg-amber-50/50 dark:bg-amber-950/20 dark:border-amber-800">
          <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex items-start gap-4 flex-1 min-w-0">
              <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/50 flex items-center justify-center flex-shrink-0">
                <Store className="w-5 h-5 text-amber-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">Seleziona l'attivita da gestire</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Account Google collegato ({connectionStatus?.google_email}). Ora seleziona quale attivita business vuoi gestire da questa dashboard.
                </p>
              </div>
            </div>
            <Button onClick={handleOpenLocationSelector} data-testid="select-location-banner-btn" className="w-full sm:w-auto">
              <Store className="w-4 h-4 mr-2" />
              Seleziona Attivita
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Fully linked banner */}
      {isFullyLinked && (
        <Card className="border-emerald-200 bg-emerald-50/50 dark:bg-emerald-950/20 dark:border-emerald-800">
          <CardContent className="p-3 flex items-center gap-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
            <p className="text-sm text-emerald-800 dark:text-emerald-200">
              Le modifiche al profilo, nome, telefono, sito web e orari vengono sincronizzate automaticamente con Google.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full sm:w-auto flex justify-start gap-1 h-auto p-1 sm:inline-flex" data-testid="gb-tabs">
          <TabsTrigger value="overview" data-testid="tab-overview" className="text-xs sm:text-sm whitespace-normal py-2"><Building2 className="w-4 h-4 mr-1.5 hidden sm:inline" />Panoramica</TabsTrigger>
          <TabsTrigger value="hours" data-testid="tab-hours" className="text-xs sm:text-sm whitespace-normal py-2"><Clock className="w-4 h-4 mr-1.5 hidden sm:inline" />Orari</TabsTrigger>
          <TabsTrigger value="description" data-testid="tab-description" className="text-xs sm:text-sm whitespace-normal py-2"><FileText className="w-4 h-4 mr-1.5 hidden sm:inline" />Descrizione</TabsTrigger>
          <TabsTrigger value="photos" data-testid="tab-photos" className="text-xs sm:text-sm whitespace-normal py-2"><Image className="w-4 h-4 mr-1.5 hidden sm:inline" />Foto</TabsTrigger>
          <TabsTrigger value="updates" data-testid="tab-updates" className="text-xs sm:text-sm whitespace-normal py-2"><Megaphone className="w-4 h-4 mr-1.5 hidden sm:inline" />Aggiornamenti</TabsTrigger>
          <TabsTrigger value="info" data-testid="tab-info" className="text-xs sm:text-sm whitespace-normal py-2"><Info className="w-4 h-4 mr-1.5 hidden sm:inline" />Info</TabsTrigger>
        </TabsList>

        {/* PANORAMICA */}
        <TabsContent value="overview" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="font-outfit text-lg">Informazioni Generali</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="bname">Nome Attivita</Label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input id="bname" className="pl-10" value={profile.business_name || ''} onChange={e => updateField('business_name', e.target.value)} placeholder="Es. Salon Beauty" data-testid="input-business-name" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bcat">Categoria</Label>
                  <div className="relative">
                    <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input id="bcat" className="pl-10" value={profile.category || ''} onChange={e => updateField('category', e.target.value)} placeholder="Es. Parrucchiere" data-testid="input-category" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="baddr">Indirizzo</Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input id="baddr" className="pl-10" value={profile.address || ''} onChange={e => updateField('address', e.target.value)} placeholder="Via Roma 1, Milano" data-testid="input-address" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bphone">Telefono</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input id="bphone" className="pl-10" value={profile.phone || ''} onChange={e => updateField('phone', e.target.value)} placeholder="+39 02 1234567" data-testid="input-phone" />
                  </div>
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="bweb">Sito Web</Label>
                  <div className="relative">
                    <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input id="bweb" className="pl-10" value={profile.website || ''} onChange={e => updateField('website', e.target.value)} placeholder="https://www.miosalone.it" data-testid="input-website" />
                  </div>
                </div>
              </div>
              <div className="flex justify-end pt-2">
                <Button onClick={saveProfile} disabled={saving} data-testid="save-profile-btn">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  {isFullyLinked ? 'Salva e Sincronizza' : 'Salva Profilo'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ORARI */}
        <TabsContent value="hours" className="mt-6">
          <Card>
            <CardHeader><CardTitle className="font-outfit text-lg">Orari di Apertura</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-3">
                {DAYS.map(({ key, label }) => {
                  const day = profile.opening_hours?.[key] || { open: '09:00', close: '18:00', closed: false };
                  return (
                    <div key={key} className="flex flex-wrap items-center gap-x-3 gap-y-2 p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors" data-testid={`hours-${key}`}>
                      <span className="w-20 sm:w-24 text-sm font-medium">{label}</span>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Switch checked={!day.closed} onCheckedChange={(v) => updateHour(key, 'closed', !v)} data-testid={`toggle-${key}`} />
                        <span className="text-xs text-muted-foreground w-12">{day.closed ? 'Chiuso' : 'Aperto'}</span>
                      </div>
                      {!day.closed && (
                        <div className="flex items-center gap-2 ml-auto sm:ml-0 w-full sm:w-auto">
                          <Input type="time" className="flex-1 sm:w-28 h-8 text-sm" value={day.open || '09:00'} onChange={e => updateHour(key, 'open', e.target.value)} data-testid={`open-${key}`} />
                          <span className="text-muted-foreground text-sm">-</span>
                          <Input type="time" className="flex-1 sm:w-28 h-8 text-sm" value={day.close || '18:00'} onChange={e => updateHour(key, 'close', e.target.value)} data-testid={`close-${key}`} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-end pt-4">
                <Button onClick={saveHours} disabled={saving} data-testid="save-hours-btn" className="w-full sm:w-auto">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  {isFullyLinked ? 'Salva e Sincronizza Orari' : 'Salva Orari'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* DESCRIZIONE */}
        <TabsContent value="description" className="mt-6">
          <Card>
            <CardHeader><CardTitle className="font-outfit text-lg">Descrizione Attivita</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <Textarea value={profile.description || ''} onChange={e => updateField('description', e.target.value)} placeholder="Descrivi la tua attivita, i servizi offerti, la tua esperienza..." rows={8} className="resize-none" data-testid="input-description" />
              <div className="flex items-center justify-between">
                <p className="text-xs text-muted-foreground">{(profile.description || '').length}/750 caratteri</p>
                <Button onClick={saveProfile} disabled={saving} data-testid="save-description-btn">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Salva Descrizione
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* FOTO */}
        <TabsContent value="photos" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-outfit text-lg">Galleria Foto</CardTitle>
              <Badge variant="outline">{(profile.photos || []).length} foto</Badge>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2 mb-4">
                <Input value={newPhotoUrl} onChange={e => setNewPhotoUrl(e.target.value)} placeholder="Inserisci URL immagine..." className="flex-1" data-testid="input-photo-url" />
                <Button onClick={addPhoto} disabled={!newPhotoUrl.trim()} data-testid="add-photo-btn"><Plus className="w-4 h-4 mr-2" /> Aggiungi</Button>
              </div>
              {(profile.photos || []).length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                  {profile.photos.map((url, i) => (
                    <div key={`photo-${url}`} className="group relative aspect-square rounded-lg overflow-hidden bg-muted" data-testid={`photo-${i}`}>
                      <img src={url} alt={`Foto ${i + 1}`} className="w-full h-full object-cover" onError={e => { e.target.style.display = 'none'; }} />
                      <button onClick={() => removePhoto(url)} className="absolute top-2 right-2 p-1.5 rounded-full bg-red-500/90 text-white opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`remove-photo-${i}`}>
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-12 text-center">
                  <Image className="w-10 h-10 mx-auto text-muted-foreground/40 mb-3" />
                  <p className="text-sm text-muted-foreground">Nessuna foto aggiunta</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* AGGIORNAMENTI */}
        <TabsContent value="updates" className="mt-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold font-outfit">Aggiornamenti Google</h2>
              <Button onClick={() => setNewPostDialog(true)} data-testid="create-update-btn"><Plus className="w-4 h-4 mr-2" /> Nuovo Aggiornamento</Button>
            </div>
            {posts.length > 0 ? (
              <div className="space-y-3">
                {posts.map((post) => (
                  <Card key={post.id} className="card-hover" data-testid={`gb-post-${post.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge variant="outline" className="capitalize text-xs">{post.post_type}</Badge>
                            <Badge variant={post.status === 'published' ? 'default' : 'secondary'} className="text-xs">{post.status === 'published' ? 'Pubblicato' : 'Bozza'}</Badge>
                          </div>
                          <p className="text-sm">{post.content}</p>
                          <p className="text-xs text-muted-foreground mt-2">
                            {post.created_at && format(parseISO(post.created_at), 'd MMM yyyy, HH:mm', { locale: it })}
                          </p>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => deletePost(post.id)} data-testid={`delete-post-${post.id}`}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card><CardContent className="py-12 text-center">
                <Megaphone className="w-10 h-10 mx-auto text-muted-foreground/40 mb-3" />
                <p className="text-sm text-muted-foreground">Nessun aggiornamento</p>
              </CardContent></Card>
            )}
          </div>
        </TabsContent>

        {/* INFO / ATTRIBUTI */}
        <TabsContent value="info" className="mt-6">
          <Card>
            <CardHeader><CardTitle className="font-outfit text-lg">Servizi e Caratteristiche</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-4">
                {ATTRIBUTE_CONFIG.map(({ key, label, icon: Icon }) => (
                  <div key={key} className="flex items-center justify-between p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors" data-testid={`attr-${key}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center"><Icon className="w-4 h-4 text-primary" /></div>
                      <span className="text-sm font-medium">{label}</span>
                    </div>
                    <Switch checked={profile.attributes?.[key] || false} onCheckedChange={(v) => updateAttr(key, v)} data-testid={`toggle-attr-${key}`} />
                  </div>
                ))}
              </div>
              <div className="flex justify-end pt-4">
                <Button onClick={saveAttributes} disabled={saving} data-testid="save-attributes-btn">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Salva Attributi
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ═══ Location Selector Dialog ═══ */}
      <Dialog open={locationDialog} onOpenChange={setLocationDialog}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-outfit">Seleziona Attivita</DialogTitle>
            <DialogDescription>Scegli l'account e la scheda Google Business da collegare.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {/* Step 1: Accounts list */}
            {!selectedAccount && (
              <>
                <p className="text-sm text-muted-foreground">Seleziona l'account Google Business che contiene l'attivita da gestire.</p>
                {loadingAccounts ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-primary mr-2" />
                    <span className="text-sm">Caricamento account...</span>
                  </div>
                ) : accounts.length > 0 ? (
                  <div className="space-y-2">
                    {accounts.map((account) => (
                      <button
                        key={account.name}
                        onClick={() => handleSelectAccount(account)}
                        className="w-full flex items-center gap-3 p-3 rounded-lg border hover:bg-muted/50 transition-colors text-left"
                        data-testid={`account-${account.name}`}
                      >
                        <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                          <Building2 className="w-5 h-5 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{account.accountName || account.name}</p>
                          <p className="text-xs text-muted-foreground">{account.type === 'PERSONAL' ? 'Account personale' : account.type || 'Account Business'}</p>
                        </div>
                        <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="py-8 text-center">
                    <Building2 className="w-8 h-8 mx-auto text-muted-foreground/40 mb-2" />
                    <p className="text-sm text-muted-foreground">Nessun account trovato</p>
                    <p className="text-xs text-muted-foreground mt-1">Verifica che l'account Google collegato gestisca almeno un profilo Business.</p>
                    <Button variant="outline" size="sm" className="mt-3" onClick={fetchAccounts}>
                      <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Riprova
                    </Button>
                  </div>
                )}
              </>
            )}

            {/* Step 2: Locations for selected account */}
            {selectedAccount && (
              <>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" onClick={() => { setSelectedAccount(null); setLocations([]); }}>
                    <ChevronRight className="w-4 h-4 rotate-180 mr-1" /> Indietro
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    {selectedAccount.accountName || selectedAccount.name}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground">Seleziona l'attivita che vuoi gestire da questa dashboard:</p>
                {loadingLocations ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-primary mr-2" />
                    <span className="text-sm">Caricamento attivita...</span>
                  </div>
                ) : locations.length > 0 ? (
                  <div className="space-y-2">
                    {locations.map((loc) => {
                      const addr = loc.storefrontAddress;
                      const addrStr = addr ? [
                        ...(addr.addressLines || []),
                        addr.locality,
                        addr.postalCode
                      ].filter(Boolean).join(', ') : '';
                      return (
                        <button
                          key={loc.name}
                          onClick={() => selectLocation(loc)}
                          disabled={selectingLocation}
                          className="w-full flex items-center gap-3 p-3 rounded-lg border hover:bg-emerald-50 dark:hover:bg-emerald-950/20 hover:border-emerald-300 transition-colors text-left disabled:opacity-50"
                          data-testid={`location-${loc.name}`}
                        >
                          <div className="w-10 h-10 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center flex-shrink-0">
                            <Store className="w-5 h-5 text-emerald-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{loc.title || 'Attivita senza nome'}</p>
                            {addrStr && <p className="text-xs text-muted-foreground truncate">{addrStr}</p>}
                            {loc.websiteUri && <p className="text-xs text-blue-500 truncate">{loc.websiteUri}</p>}
                          </div>
                          {selectingLocation ? (
                            <Loader2 className="w-4 h-4 animate-spin text-primary flex-shrink-0" />
                          ) : (
                            <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="py-8 text-center">
                    <Store className="w-8 h-8 mx-auto text-muted-foreground/40 mb-2" />
                    <p className="text-sm text-muted-foreground">Nessuna attivita trovata in questo account</p>
                    <Button variant="outline" size="sm" className="mt-3" onClick={() => fetchLocations(selectedAccount.name)}>
                      <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Riprova
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* New Post Dialog */}
      <Dialog open={newPostDialog} onOpenChange={setNewPostDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-outfit">Nuovo Aggiornamento Google</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Tipo</Label>
              <Select value={newPost.post_type} onValueChange={v => setNewPost(p => ({ ...p, post_type: v }))}>
                <SelectTrigger data-testid="select-post-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="update">Aggiornamento</SelectItem>
                  <SelectItem value="offer">Offerta</SelectItem>
                  <SelectItem value="event">Evento</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {newPost.post_type === 'event' && (
              <div className="space-y-2">
                <Label>Titolo Evento</Label>
                <Input value={newPost.event_title} onChange={e => setNewPost(p => ({ ...p, event_title: e.target.value }))} placeholder="Es. Open Day" data-testid="input-event-title" />
              </div>
            )}
            <div className="space-y-2">
              <Label>Contenuto</Label>
              <Textarea value={newPost.content} onChange={e => setNewPost(p => ({ ...p, content: e.target.value }))} placeholder="Scrivi il tuo aggiornamento..." rows={4} data-testid="input-post-content" />
            </div>
            <div className="space-y-2">
              <Label>URL Immagine (opzionale)</Label>
              <Input value={newPost.media_url} onChange={e => setNewPost(p => ({ ...p, media_url: e.target.value }))} placeholder="https://..." data-testid="input-post-media" />
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setNewPostDialog(false)}>Annulla</Button>
              <Button onClick={createPost} disabled={!newPost.content.trim()} data-testid="submit-post-btn">Crea Aggiornamento</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GoogleBusinessPage;
